/**
 * Owner: Masa Bayu
 * File: useLayoutClass.ts
 * Date:Time: 2026-09-17 22:34
 * Description: UI-01 layout class hook — Desktop / Tablet / Mobile (not shrink-only).
 */

import { useEffect, useState } from 'react';

export type QxLayoutClass = 'desktop' | 'tablet' | 'mobile';

export function resolveLayoutClass(width: number): QxLayoutClass {
  if (width <= 767) {
    return 'mobile';
  }
  if (width <= 1023) {
    return 'tablet';
  }
  return 'desktop';
}

/**
 * §373 QX Responsive UI Standard chrome bands:
 * mobile ≤767 · tablet 768–1023 · desktop ≥1024.
 * Syncs from viewport width (resize); does not rely on matchMedia alone.
 */
export function useLayoutClass(): QxLayoutClass {
  const [layout, setLayout] = useState<QxLayoutClass>(() => {
    if (typeof window === 'undefined') {
      return 'desktop';
    }
    return resolveLayoutClass(window.innerWidth);
  });

  useEffect(() => {
    const sync = () => {
      setLayout(resolveLayoutClass(window.innerWidth));
    };
    sync();
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('resize', sync);
    };
  }, []);

  return layout;
}

export function layoutClassAttr(layout: QxLayoutClass): string {
  return `qx-layout qx-layout--${layout}`;
}
